import * as yup from "yup";
import { setLocale } from "yup";
import { MAX_IMAGE_SIZE, MAX_VIDEO_SIZE, MAX_ZIP_SIZE } from "@";

// Set custom validation messages in Arabic
setLocale({
  mixed: {
    default: "هذا الحقل غير صالح",
    required: "هذا الحقل مطلوب",
    oneOf: "القيمة يجب أن تكون إحدى القيم المحددة",
    notOneOf: "القيمة يجب ألا تكون إحدى القيم المحددة",
  },
  string: {
    length: "النص يجب أن يكون بطول ${length} حروف",
    min: "النص يجب أن يكون على الأقل ${min} حروف",
    max: "النص يجب أن يكون على الأكثر ${max} حروف",
    matches: "النص يجب أن يطابق الشكل المطلوب",
    email: "البريد الإلكتروني غير صالح",
    url: "الرابط غير صالح",
    trim: "النص يجب ألا يحتوي على مسافات في البداية أو النهاية",
    lowercase: "النص يجب أن يكون بحروف صغيرة",
    uppercase: "النص يجب أن يكون بحروف كبيرة",
  },
  number: {
    min: "القيمة يجب أن تكون على الأقل ${min}",
    max: "القيمة يجب أن تكون على الأكثر ${max}",
    lessThan: "القيمة يجب أن تكون أقل من ${less}",
    moreThan: "القيمة يجب أن تكون أكثر من ${more}",
    positive: "القيمة يجب أن تكون موجبة",
    negative: "القيمة يجب أن تكون سالبة",
    integer: "القيمة يجب أن تكون عدد صحيح",
  },
  date: {
    min: "التاريخ يجب أن يكون بعد ${min}",
    max: "التاريخ يجب أن يكون قبل ${max}",
  },
  array: {
    min: "القائمة يجب أن تحتوي على الأقل على ${min} عناصر",
    max: "القائمة يجب أن تحتوي على الأكثر على ${max} عناصر",
  },
});

// Function to validate different types of fields
export function validate(type) {
  switch (type) {
    case "image":
      return yup
        .mixed()
        .required("هذا الحقل مطلوب")
        .test("fileRequired", "يجب رفع صورة", (value) => value)
        .test(
          "fileSize",
          `يجب أن يكون حجم الصورة أقل من ${MAX_IMAGE_SIZE / 1024 / 1024}MB`,
          (value) => value.size <= MAX_IMAGE_SIZE,
        )
        .label("image");

    case "iban":
      return yup
        .string()
        .required()
        .trim()
        .matches(
          /^SA\d{2}[A-Z0-9]{18,20}$/,
          "صيغة IBAN غير صالحة. يجب أن يبدأ IBAN بـ 'SA' متبوعًا بـ 20 أو 22 رقمًا ",
        )
        .label("iban");

    case "video":
      return yup
        .mixed()
        .required("هذا الحقل مطلوب")
        .test("fileRequired", "يجب رفع فديو", (value) => value)
        .test(
          "fileSize",
          `يجب أن يكون حجم الفديو أقل من ${MAX_VIDEO_SIZE / 1024 / 1024}MB`,
          (value) => value.size <= MAX_VIDEO_SIZE,
        )
        .label("video");

    case "nullable-image":
      return yup
        .mixed()
        .nullable()
        .test(
          "fileSize",
          `يجب أن يكون حجم الصورة أقل من ${MAX_IMAGE_SIZE / 1024 / 1024}MB`,
          (value) => (value ? value.size <= MAX_IMAGE_SIZE : true),
        );

    case "nullable-images":
      return yup
        .array()
        .nullable()
        .of(
          yup
            .mixed()
            .test(
              "fileType",
              "تنسيق الملف غير مدعوم اختر ملف من نوع صورة",
              (value) => !value || value.type?.startsWith("image/"),
            )
            .test(
              "fileSize",
              `يجب أن يكون حجم الصورة أقل من ${MAX_IMAGE_SIZE / 1024 / 1024}MB`,
              (value) => !value || value.size <= MAX_IMAGE_SIZE,
            ),
        );

    case "nullable-videos":
      return yup
        .array()
        .nullable()
        .of(
          yup
            .mixed()
            .test(
              "fileType",
              "تنسيق الملف غير مدعوم اختر ملف من نوع فديو",
              (value) => !value || value.type?.startsWith("video/"),
            )
            .test(
              "fileSize",
              `يجب أن يكون حجم الفديو أقل من ${MAX_VIDEO_SIZE / 1024 / 1024}MB`,
              (value) => !value || value.size <= MAX_VIDEO_SIZE,
            ),
        );

    case "nullable-zip":
      return yup
        .array()
        .nullable()
        .max(1, "يمكن رفع ملف مضغوط واحد فقط")
        .of(
          yup
            .mixed()
            .test(
              "fileType",
              "تنسيق الملف غير مدعوم اختر ملف من النوع zip",
              (value) =>
                !value ||
                [
                  "application/zip",
                  "application/x-zip-compressed",
                  "application/x-zip",
                ].includes(value.type),
            )
            .test(
              "fileSize",
              `يجب أن يكون حجم الملف المضغوط أقل من ${MAX_ZIP_SIZE / 1024 / 1024}MB`,
              (value) => !value || value.size <= MAX_ZIP_SIZE,
            ),
        );

    case "xlsx":
      return yup
        .mixed()
        .required("رجاءا اختيار الملف")
        .test(
          "fileType",
          "تنسيق الملف غير مدعوم اختر ملف من النوع xlsx",
          (value) => {
            return (
              !value ||
              (value &&
                [
                  "application/vnd.ms-excel",
                  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                ].includes(value.type))
            );
          },
        );

    case "url":
      return yup
        .string()
        .matches(
          /^(https?:\/\/)?(www\.)?([\w-]+(?:\.[\w-]+)+)(?:[\w.,@?^=%&:\/~+#-]*[\w@?^=%&\/~+#-])?$/,
          {
            message:
              "ادخل رابط صالح (example.com أو www.example.com أو https://example.com أو https://www.example.com )",
            excludeEmptyString: true,
          },
        )
        .trim()
        .required()
        .label("url");

    case "nullableUrl":
      return yup
        .string()
        .nullable()
        .trim()
        .matches(
          /^(https?:\/\/)?(www\.)?([\w-]+(?:\.[\w-]+)+)(?:[\w.,@?^=%&:\/~+#-]*[\w@?^=%&\/~+#-])?$/,
          {
            message:
              "ادخل رابط صالح (example.com أو www.example.com أو https://example.com أو https://www.example.com )",
            excludeEmptyString: true,
          },
        )
        .label("url");

    case "email":
      return yup.string().required().trim().email().label("email");

    case "number":
      return yup
        .number()
        .required("هذا الحقل مطلوب")
        .transform((value, originalValue) =>
          originalValue === "" ? null : value,
        )
        .typeError("هذا الحقل يجب أن يكون عدد صحيح")
        .positive("هذا الحقل يجب أن يكون عدد موجب")
        .min(0, "الحد الأدنى يجب أن يكون 0")
        .label("number");

    case "numberWithValue":
      return yup
        .number()
        .required("هذا الحقل مطلوب")
        .transform((value, originalValue) =>
          originalValue === "" ? null : value,
        )
        .typeError("هذا الحقل يجب أن يكون عدد صحيح")
        .positive("هذا الحقل يجب أن يكون عدد موجب")
        .min(1, "الحد الأدنى يجب أن يكون 1")
        .label("number");

    case "nullableEmail":
      return yup
        .string()
        .nullable()
        .trim()
        .test("is-valid-email", "أدخل عنوان بريد إلكتروني صالح", (value) => {
          // If the value is null or an empty string, skip validation
          if (!value) return true;

          // Perform the email validation
          return yup.string().email().isValidSync(value);
        })
        .label("email");

    case "nullablePhone":
      return yup
        .string()
        .nullable()
        .trim()
        .matches(/^\+?(\d{1,3})?\d{7,14}$/, "يجب ادخال رقم هاتف صالح ")
        .test("is-valid-phone", "يجب ادخال رقم هاتف صالح ", (value) => {
          // If the value is null or an empty string, skip validation
          if (!value) return true;

          // Perform the length checks
          return value.length >= 10 && value.length <= 13;
        })
        .label("phone");

    case "phone":
      return yup
        .string()
        .required()
        .trim()
        .matches(/^\+?(\d{1,3})?\d{7,14}$/, "يجب ادخال رقم هاتف صالح ")
        .test("is-valid-phone", "يجب ادخال رقم هاتف صالح ", (value) => {
          // Perform the length checks
          return value.length >= 10 && value.length <= 13;
        })
        .label("phone");

    case "req":
      return yup.string().required().min(1).trim().label("required");

    case "array":
      return yup.array().min(1).required().label("array");

    case "date":
      return yup.date().required().label("date");

    case "password":
      return yup
        .string()
        .required()
        .trim()
        .matches(
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*_=+-]).{8,20}$/,
          "8 احرف مع (أحرف صغيرة ، أحرف كبيرة ، ارقام و رموز)",
        )
        .label("password");

    // TODO complete validation for nullable password
    case "nullablePassword":
      return yup
        .string()
        .nullable()
        .trim()
        .matches(
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*_=+-]).{8,20}$/,
          "8 احرف مع (أحرف صغيرة ، أحرف كبيرة ، ارقام و رموز)",
        )
        .label("nullablePassword");
  }
}

// Function to create a validation schema
export const createValidationSchema = (fields) => {
  return yup.object(fields).required();
};

// validate if the field is file
export const validateIfFile = (validationType = "image", field) =>
  yup.string().when([field], {
    is: (value) => value == null || value === "" || value === undefined,
    then: () => validate(validationType),
    otherwise: () => yup.mixed().nullable(),
  });

// Schema for validating import data
export const importDataSchema = createValidationSchema({
  file: validate("xlsx"),
  importTo: validate("req"),

  importFrom: yup.string().when(["importTo"], {
    is: (importTo) => importTo === "order",
    then: () => yup.string().nullable(),
    otherwise: () => validate("req"),
  }),
  orderDetailsFile: yup.string().when(["importTo"], {
    is: (importTo) => importTo === "order",
    then: () => validate("xlsx"),
    otherwise: () => yup.mixed().nullable(),
  }),
});

export const importDataValues = {
  file: null,
  importTo: "",
  importFrom: "",
  orderDetailsFile: null,
};
